// src/components/IncidentSidebar.js - WITH PREMIUM LOADERS
import React, { useState, useEffect, useRef } from 'react';
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { fetchDangerZones, createDangerZoneReport } from '../../pages/home/_redux/dashboard/Actions';

function IncidentSidebar({ mode, selectedIncident, toggleSidebar, onIncidentSelect }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [reportForm, setReportForm] = useState({
    type: 'robbery',
    location: '',
    date: new Date().toISOString().slice(0, 16),
    details: ''
  });
  
  // Geocoding dropdown state
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const dropdownRef = useRef(null);

  // Redux selectors
  const { incidents, listLoading } = useSelector(
    (state) => ({
      incidents: state.dashboard.danger_zones || [],
      listLoading: state.dashboard.listLoading
    }),
    shallowEqual
  );

  // Fetch incidents when switching to feed mode
  useEffect(() => {
    if (mode === 'feed') {
      dispatch(fetchDangerZones({
        hours: 24,
        limit: 20,
        severity_min: 1
      }));
    }
  }, [mode, dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchLocations = async (query) => {
    if (!query || query.length < 3) {
      setLocationSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setGeocoding(true);

    try {
      const nominatimUrl = `https://nominatim.openstreetmap.org/search?` + 
        `q=${encodeURIComponent(query + ', Nigeria')}` +
        `&format=json` +
        `&limit=5` +
        `&countrycodes=ng` +
        `&addressdetails=1`;

      const response = await fetch(nominatimUrl, {
        headers: {
          'User-Agent': 'SetyIntel/1.0'
        }
      });

      const data = await response.json();

      if (data && data.length > 0) {
        const suggestions = data.map(item => ({
          name: item.display_name,
          coordinates: [parseFloat(item.lon), parseFloat(item.lat)],
          type: item.type,
          importance: item.importance
        }));

        setLocationSuggestions(suggestions);
        setShowSuggestions(true);
      } else {
        setLocationSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Geocoding failed:', error);
      setLocationSuggestions([]);
    } finally {
      setGeocoding(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (reportForm.location && !selectedLocation) {
        searchLocations(reportForm.location);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [reportForm.location, selectedLocation]);

  const handleLocationSelect = (suggestion) => {
    setSelectedLocation(suggestion);
    setReportForm({
      ...reportForm,
      location: suggestion.name
    });
    setShowSuggestions(false);
    setLocationSuggestions([]);
  };

  const handleLocationChange = (e) => {
    setReportForm({
      ...reportForm,
      location: e.target.value
    });
    setSelectedLocation(null);
  };

  const handleReportSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!selectedLocation) {
        alert('⚠️ Please select a location from the dropdown suggestions');
        setLoading(false);
        return;
      }

      const reportData = {
        type: reportForm.type,
        address: selectedLocation.name,
        coordinates: selectedLocation.coordinates,
        date: new Date(reportForm.date).toISOString(),
        description: reportForm.details
      };

      await dispatch(createDangerZoneReport(reportData));
      
      setReportForm({
        type: 'robbery',
        location: '',
        date: new Date().toISOString().slice(0, 16),
        details: ''
      });
      setSelectedLocation(null);

      dispatch(fetchDangerZones());

    } catch (error) {
      console.error('Report submission failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Premium Skeleton Loader Component
  const SkeletonCard = () => (
    <div className="skeleton-card">
      <div className="skeleton-severity"></div>
      <div className="skeleton-content">
        <div className="skeleton-header">
          <div className="skeleton-badge"></div>
          <div className="skeleton-time"></div>
        </div>
        <div className="skeleton-location"></div>
        <div className="skeleton-text"></div>
        <div className="skeleton-text short"></div>
        <div className="skeleton-button"></div>
      </div>
    </div>
  );

  // Render different modes
  if (mode === 'feed') {
    return (
      <div className="sidebar-inner">
        <div className="feed-container">
          <div className="feed-status">
            <span>Showing {incidents.length} Recent Alerts</span>
            <div className="pulse-container">
              <div className="pulse-dot"></div>
              <span>Live</span>
            </div>
          </div>


 
          {listLoading && incidents.length === 0 ? (
            <div >
              {[1, 2, 3, 4].map((item) => (
                <SkeletonCard key={item} />
              ))}
            </div>
          ) : incidents.length === 0 ? (
            <div className="empty-state">
              <i className="fa-solid fa-shield-check"></i>
              <h4>All Clear</h4>
              <p>No recent incidents in the past 24 hours</p>
            </div>
          ) : (
            <>
              <div className="incident-list">
                {incidents.map((incident) => (
                  <div 
                    key={incident.id} 
                    className={`incident-card ${selectedIncident?.id === incident.id ? 'selected' : ''}`}
                    onClick={() => {
                      toggleSidebar();
                      onIncidentSelect(incident);
                    }}
                  >
                    <div className={`severity-bar ${incident.severity}`}></div>
                    <div className="card-content">
                      <div className="card-meta">
                        <div className="badge-group">
                          <span className={`badge badge-${incident.severity}`}>
                            {incident.type}
                          </span>
                          {incident.verified && (
                            <span className="badge badge-verified">
                              <i className="fa-solid fa-shield-check"></i> Verified
                            </span>
                          )}
                        </div>
                        <span className="timestamp">{incident.time}</span>
                      </div>
                      <h4 className="location-name">
                        <i className="fa-solid fa-location-dot"></i>
                        {incident.location}
                      </h4>
                      <p className="excerpt">{incident.details}</p>
                      <button className="view-btn">
                        View on Map <i className="fa-solid fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="app-nudge">
                <a href="https://onelink.to/unm2ez" className="app-nudge-btn" target="_blank" rel="noopener noreferrer">
                  <i className="fa-brands fa-google-play"></i>
                  <i className="fa-brands fa-app-store-ios"></i>
                  Download App for more insight
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (mode === 'predict') {
    return (
      <div className="sidebar-inner">
        <div className="predict-container">
          <div className="coming-soon-container">
            <div className="coming-soon-icon">
              <i className="fa-solid fa-brain"></i>
            </div>
            <h3>AI Risk Prediction</h3>
            <p className="coming-soon-text">
              Our advanced machine learning models are being trained on 3+ years of security incident data.
              Get ready for predictive intelligence that could save lives.
            </p>

            <div className="coming-soon-footer">
              <span className="status-badge">
                <span className="pulse-dot"></span>
                Only available on the Mobile app or Sety Intel Dashboard
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'report') {
    return (
      <div className="sidebar-inner">
        <div className="report-container">
          <div className="report-header">
            <h3>
              <i className="fa-solid fa-bullhorn"></i>
              Broadcast Intelligence
            </h3>
            <p>Your report will be verified by our AI and the Sety Responder network.</p>
          </div>
          
          <form className="pro-form" onSubmit={handleReportSubmit}>
            <div className="form-group">
              <label>Incident Type</label>
              <select 
                className="custom-input"
                value={reportForm.type}
                onChange={(e) => setReportForm({...reportForm, type: e.target.value})}
                required
              >
                <option value="robbery">Armed Robbery</option>
                <option value="kidnapping">Kidnapping</option>
                <option value="accident">Accident</option>
                <option value="murder">Murder</option>
                <option value="fire">Fire</option>
                <option value="protest">Civil Unrest / Protest</option>
                <option value="violent_protest">Violent Protest</option>
                <option value="assault">Assault / Attack</option>
                <option value="terrorism">Terrorism / Bombing</option>
                <option value="armed_conflict">Armed Conflict</option>
                <option value="infrastructure">Infrastructure Failure</option>
                <option value="health">Health Emergency</option>
                <option value="natural_disaster">Natural Disaster</option>
                <option value="other">Other Security Threat</option>
              </select>
            </div>

            <div className="form-group" ref={dropdownRef}>
              <label>
                Location 
                {geocoding && <span className="geocoding-indicator">(Searching...)</span>}
                {selectedLocation && <span className="location-selected-indicator"><i className="fa-solid fa-check"></i> Selected</span>}
              </label>
              <div className="input-with-icon">
                <i className="fa-solid fa-location-dot"></i>
                <input 
                  type="text" 
                  className="custom-input" 
                  placeholder="Start typing location (e.g., Ikeja, Lagos)"
                  value={reportForm.location}
                  onChange={handleLocationChange}
                  required
                  disabled={loading}
                  autoComplete="off"
                />
              </div>

              {showSuggestions && locationSuggestions.length > 0 && (
                <div className="location-dropdown">
                  {locationSuggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      className="location-dropdown-item"
                      onClick={() => handleLocationSelect(suggestion)}
                    >
                      <i className="fa-solid fa-location-dot"></i>
                      <div className="location-dropdown-text">
                        <span className="location-main">{suggestion.name.split(',')[0]}</span>
                        <span className="location-sub">{suggestion.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <small className="input-help">
                <i className="fa-solid fa-circle-info"></i>
                Type to search and select from suggestions
              </small>
            </div>

            <div className="form-group">
              <label>
                Date & Time
                <span className="optional-label">(Optional - defaults to now)</span>
              </label>
              <div className="input-with-icon">
                <i className="fa-solid fa-calendar-days"></i>
                <input 
                  type="datetime-local" 
                  className="custom-input" 
                  value={reportForm.date}
                  onChange={(e) => setReportForm({...reportForm, date: e.target.value})}
                  max={new Date().toISOString().slice(0, 16)}
                  disabled={loading}
                />
              </div>
              <small className="input-help">
                <i className="fa-solid fa-circle-info"></i>
                When did this incident occur? Leave as-is for "now"
              </small>
            </div>

            <div className="form-group">
              <label>Incident Details</label>
              <textarea 
                className="custom-input" 
                rows="4" 
                placeholder="What happened? How many people involved? Any injuries?"
                value={reportForm.details}
                onChange={(e) => setReportForm({...reportForm, details: e.target.value})}
                required
                disabled={loading}
                minLength={20}
              ></textarea>
              <small className="input-help">
                Minimum 20 characters for AI verification
              </small>
            </div>

            <button 
              type="submit" 
              className="submit-alert-btn" 
              disabled={loading || !selectedLocation}
            >
              {loading ? (
                <>
                  <div className="btn-spinner"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-tower-broadcast"></i>
                  Broadcast Alert
                </>
              )}
            </button>

            <div className="privacy-notice">
              <i className="fa-solid fa-shield-halved"></i>
              <small>Reports are anonymous. We protect your identity while verifying intelligence.</small>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return null;
}

export default IncidentSidebar;